import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BellRing, Navigation } from "lucide-react";

const TYPE_LABELS = { audible: "Audible SOS", medical: "Medical", fire: "Fire", accident: "Accident", sos: "SOS" };

// Real-time in-app alert for signed-in trusted contacts (polls active incidents).
export const IncidentAlerts = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = () => api.get("/incidents/for-me").then(({ data }) => alive && setItems(data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const respond = async (id, response) => {
    try {
      await api.post(`/incidents/${id}/respond`, { response });
      setItems((prev) => prev.map((i) => (i.journey_id === id ? { ...i, my_response: response } : i)));
    } catch { /* incident may have ended */ }
  };

  if (!items.length) return null;

  return (
    <div className="px-4 pt-3 space-y-3" data-testid="incident-alerts">
      {items.map((i) => {
        const dir = i.location ? `https://www.google.com/maps/dir/?api=1&destination=${i.location.lat},${i.location.lng}` : null;
        const first = (i.victim_name || "").split(" ")[0] || "them";
        return (
          <div key={i.journey_id} data-testid="incident-alert-card" className="bg-[#DC2626] text-white rounded-2xl p-4 shadow-[0_10px_30px_rgba(220,38,38,0.35)]">
            <div className="flex items-start gap-2">
              <BellRing className="w-5 h-5 mt-0.5 shrink-0 animate-pulse" />
              <div className="flex-1">
                <p className="font-black leading-tight" data-testid="incident-victim">{i.victim_name} triggered an SOS</p>
                <p className="text-xs text-white/80 mt-0.5">{TYPE_LABELS[i.type] || "SOS"}{i.destination ? ` · heading to ${i.destination}` : ""}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {dir && (
                <a data-testid="incident-navigate" href={dir} target="_blank" rel="noreferrer" className="col-span-2 rounded-xl py-2.5 bg-white/15 font-bold text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Navigation className="w-4 h-4" /> Navigate to {first}
                </a>
              )}
              {i.my_response ? (
                <div className="col-span-2 text-center text-sm font-bold bg-white/15 rounded-xl py-2.5" data-testid="incident-my-response">
                  {i.my_response === "responding" ? "You're responding — stay safe" : "You marked: can't respond"}
                </div>
              ) : (
                <>
                  <button data-testid="incident-respond-yes" onClick={() => respond(i.journey_id, "responding")} className="rounded-xl py-2.5 bg-white text-[#DC2626] font-black text-sm active:scale-95 transition-transform">I'm Responding</button>
                  <button data-testid="incident-respond-no" onClick={() => respond(i.journey_id, "cant")} className="rounded-xl py-2.5 bg-white/15 font-bold text-sm active:scale-95 transition-transform">Can't Respond</button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
